import type { Messages } from '../catalogue.en'

/**
 * Shell, identity and the messages every surface can reach: the app frame,
 * language switching, sign-in, navigation labels and the generic error and
 * not-found states.
 *
 * COUNTS DO NOT INFLECT HERE, AND THAT RULE IS THE WHOLE RUSSIAN CATALOGUE'S.
 * Russian agrees a counted noun with the number in three forms — 1 задача,
 * 3 задачи, 5 задач — and `translate` interpolates without agreeing. An id
 * whose English reads `{count} tasks` therefore reads `Задач: {count}` in
 * Russian: the counter form is correct for every number, where any inflected
 * form is wrong for most of them. Only a string the caller reaches at exactly
 * one — the `…One` half of a count pair — may name the noun beside the digit.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const common: Partial<Messages> = {
  "clinicMaintenance.warning": "Работы начнутся {deadline} (через {seconds} сек.). Пожалуйста, сохраните изменения и выйдите из клиники.",
  "clinicMaintenance.closed": "В клинике ведутся технические работы",
  "clinicMaintenance.description": "Доступ вернётся после завершения работ. Несохранённые изменения остаются на этой странице. Можно сменить клинику или выйти из аккаунта.",
  "clinicMaintenance.recheck": "Проверить снова",
  "clinicMaintenance.choose": "Сменить клинику",
  "clinicMaintenance.developer": "Технические работы: доступ разработчика",
  'organizations.choose': 'Выберите организацию',
  'organizations.notificationUnavailable': 'Уведомление недоступно или у вас больше нет доступа к нему.',
  'organizations.empty': 'Для этого аккаунта нет доступных организаций.',
  'organizations.add': 'Добавить организацию',
  'organizations.createTitle': 'Новая организация',
  'organizations.createUnsaved': 'Данные новой организации',
  'organizations.createOptionsFailed': 'Создание организации недоступно.',
  'organizations.createForbidden': 'Вам не разрешено создавать организации.',
  'organizations.name': 'Название организации',
  'organizations.calendar': 'Рабочий календарь',
  'organizations.timeZone': 'Часовой пояс',
  'organizations.defaultLanguage': 'Язык по умолчанию',
  'organizations.createConflict': 'Этот запрос уже сохранён с другими данными.',
  'organizations.createFailed': 'Не удалось создать организацию.',
  'organizations.creating': 'Создание',
  'organizations.createSubmit': 'Создать организацию',
  'organizations.createCancel': 'Отмена',
  'app.generationChanged': 'Данные приложения были заменены. Эта страница больше не может сохранять изменения. Скопируйте несохранённый текст перед перезагрузкой.',
  'app.generationReload': 'Перезагрузить приложение',
  'app.name': 'Terysling',
  'app.descriptor': 'Клиническое рабочее пространство',
  'app.skipToContent': 'Перейти к основному содержимому',
  'app.documentTitle': '{page} — {brand}',
  'app.moduleWorkspace': 'Рабочее пространство модуля',
  'app.loading': 'Загрузка',
  'app.sessionEnded': 'Ваш сеанс завершён. Возвращаемся к экрану входа…',

  'app.developedBy': 'Разработано',
  'app.developer': 'Mislab IL',

  'releaseVerification.cancel': 'Продолжить проверку',
  'releaseVerification.finish': "Завершить проверку и открыть доступ",
  'releaseVerification.confirm': "Это откроет прод для включённых пользователей и запустит фоновые обработчики. Приложение перезапустится. Несохранённые формы на этой странице останутся открытыми.",
  'releaseVerification.confirmAction': "Открыть прод",
  'releaseVerification.progress': "Открываем доступ. Соединение восстановится автоматически…",
  'releaseVerification.loading': "Получаем статус релиза…",
  'releaseVerification.unavailable': "Результат не подтверждён. Проверьте статус перед повторной попыткой.",
  'releaseVerification.forbidden': "Завершить проверку может только разработчик.",
  'releaseVerification.stale': "Релиз изменился. Обновите его статус перед продолжением.",
  'releaseVerification.failed': "Завершение не удалось. Обратитесь к оператору релиза; существующие данные сохранены.",
  'releaseVerification.checkStatus': "Проверить статус",
  'releaseVerification.refreshFailed': "Релиз завершён, но сессию обновить не удалось. Повторите проверку.",
  'releaseVerification.bannerLabel': 'Проверка релиза',
  'releaseVerification.eyebrow': 'Проверка продакшн-релиза',
  'releaseVerification.banner': 'Идёт проверка релиза. Всё созданное или изменённое сейчас может быть потеряно.',
  'releaseVerification.title': 'Идёт проверка продакшн-релиза',
  'releaseVerification.description':
    'Пока проверяется переключение продакшна, вход разрешён только пользователям с доступом к maintenance релиза. Если релиз откатят, работа, созданная в этом окне, может быть удалена.',
  'releaseVerification.acknowledge': 'Понимаю',

  'language.label': 'Язык',
  'language.en': 'Английский',
  'language.he': 'Иврит',
  'language.ru': 'Русский',
  'language.nativeEn': 'English',
  'language.nativeHe': 'עברית',
  'language.nativeRu': 'Русский',
  'language.saving': 'Сохранение языка',
  'language.saveFailed': 'Язык не сохранён. Попробуйте ещё раз.',

  'combobox.placeholder': 'Не выбрано',
  'combobox.search': 'Поиск',
  'combobox.empty': 'Нет совпадений',
  'combobox.searching': 'Поиск…',
  'combobox.chosenCount': 'Выбрано: {count}',
  'combobox.unchoose': 'Убрать {name}',

  'passwordInput.show': 'Показать пароль',
  'passwordInput.showSecret': 'Показать секрет',
  'passwordInput.policyHint': 'Длина пароля в символах: от {min} до {max}.',
  'passwordInput.requireDigit': 'Добавьте хотя бы одну цифру.',
  'passwordInput.requireUppercase': 'Добавьте хотя бы одну заглавную букву.',
  'passwordInput.requireSpecial': 'Добавьте хотя бы один спецсимвол (знак пунктуации или символ).',
  'passwordInput.policyUnavailable': 'Не удалось загрузить требования к паролю. Обновите страницу, чтобы попробовать снова.',

  'panel.close': 'Закрыть',
  'sheet.done': 'Готово',

  'codePicker.tabs': 'Где искать код',
  'codePicker.tabPersonal': 'Мои коды',
  'codePicker.searchLabel': 'Найти код',
  'codePicker.star': 'Сохранить {name} в мои коды',
  'codePicker.unstar': 'Убрать {name} из моих кодов',
  'tree.expandNamed': 'Развернуть {name}',
  'tree.collapseNamed': 'Свернуть {name}',
  'tree.enterNamed': 'Открыть {name}',
  'tree.back': 'Назад',
  'tree.renameNamed': 'Новое имя для {name}',
  'codePicker.selectNamed': 'Использовать {name}',
  'codePicker.personalLoading': 'Загрузка…',
  'codePicker.personalLoadFailed': 'Не удалось прочитать ваши сохранённые коды.',
  'codePicker.personalEmpty': 'Вы ещё не сохранили ни одного кода.',
  'codePicker.personalEmptyDetail': 'Отметьте код звёздочкой в каталоге, и он появится здесь.',
  'codePicker.personalEmptyNoGrant': 'Ваша учётная запись не может сохранять коды в этот список.',
  'codePicker.shortlist': 'Избранные',
  'codePicker.folderName': 'Новая папка',
  'codePicker.folderCreate': 'Добавить папку',
  'codePicker.folderEmpty': 'Эта папка пуста.',
  'codePicker.showLabel': 'Показать',
  'codePicker.showAll': 'Всё',
  'codePicker.showFolders': 'Только папки',
  'codePicker.showCodes': 'Только коды',
  'codePicker.shortlistNoMatches': 'Среди сохранённых кодов совпадений нет.',
  'codePicker.itemMenu': 'Действия для {name}',
  'codePicker.menuMoveTo': 'Переместить в',
  'codePicker.menuRemove': 'Убрать из моих кодов',
  'codePicker.folderMenu': 'Действия для папки {name}',
  'codePicker.menuRename': 'Переименовать папку',
  'codePicker.menuDelete': 'Удалить папку и коды в ней',
  'codePicker.folderNameTaken': 'У вас уже есть папка с таким названием.',
  'codePicker.forbidden': 'У вас нет прав менять ваши сохранённые коды.',
  'codePicker.gone': 'Эта папка или код больше не сохранены.',
  'codePicker.invalid': 'Этот код невозможно сохранить.',
  'codePicker.writeFailed': 'Не удалось изменить ваши сохранённые коды. Попробуйте ещё раз.',

  'table.columnFilter': 'Фильтр: {column}',
  'table.columnFilterReset': 'Очистить фильтр: {column}',
  'table.columnFilterSearch': 'Поиск',
  'table.columnFilterSelectVisible': 'Выбрать видимые',
  'table.columnFilterClearVisible': 'Снять с видимых',
  'table.columnFilterClear': 'Очистить',
  'table.columnFilterNoValues': 'Нет значений',
  'table.instantFilterPlaceholder': 'Поиск: {columns}',
  'table.instantFilterLabel': 'Поиск в «{table}»: {columns}',
  'table.filterContains': 'Содержит',
  'table.criterionApply': 'Применить',
  'table.criterionHint': 'Любая часть значения или /шаблон/ для регулярного выражения.',
  'table.criterionTooLong': 'Критерий слишком длинный.',
  'table.criterionInvalidPattern': 'Это недействительное регулярное выражение.',
  'table.criterionNoMatch': 'В этом столбце нет совпадающих значений.',
  'table.criterionOnDate': 'Сопоставляется с датой в формате ГГГГ-ММ-ДД.',
  'table.criterionOnInstant': 'Сопоставляется с датой и временем в показанном виде.',
  'table.criterionOnBytes': 'Сопоставляется с размером в байтах.',
  'table.criterionOnValues': 'Применение отмечает каждое совпадающее значение.',
  'table.criterionOnShownValue': 'Сопоставляется со значением в показанном виде.',
  'table.rangeFrom': '{column} с',
  'table.rangeTo': '{column} по',
  'table.resetFilters': 'Очистить фильтры',
  'table.resetFiltersOf': 'Очистить фильтры таблицы «{table}»',
  'table.add': 'Добавить',
  'table.hideColumn': 'Скрыть {column}',
  'table.columns': 'Столбцы',
  'table.columnsChoose': 'Выбрать столбцы «{table}»',
  'table.columnsChooseHidden': 'Выбрать столбцы «{table}» — часть скрыта',
  'table.columnsChooseFiltering': 'Выбрать столбцы «{table}» — скрытый столбец фильтрует таблицу',
  'table.columnFixed': 'Показан всегда',
  'table.columnLast': 'Последний столбец таблицы',
  'table.columnNarrowed': 'Столбец «{column}» скрыт и продолжает фильтровать таблицу',
  'table.expandRow': 'Развернуть {name}',
  'table.collapseRow': 'Свернуть {name}',
  'table.expandGroup': 'Развернуть группу {name}',
  'table.collapseGroup': 'Свернуть группу {name}',
  'table.selectRow': 'Выбрать {name}',
  'table.selectShown': 'Выбрать показанные строки',
  'table.clearSelection': 'Очистить выделение',
  'table.filters': 'Фильтры',
  'table.filterSheet': 'Фильтры таблицы {table}',
  'table.rowActions': 'Действия для {name}',
  'table.bulkRefused': 'Сервер отклонил: {names}.',
  'table.bulkRefusedMore': 'Сервер отклонил: {names} и ещё {count}.',
  'table.bulkRefusedDetail': 'Все невыполненные строки остаются выделенными.',
  'table.notSet': 'Не задано',
  'table.pageRow': 'Строка {from}',
  'table.pageRows': 'Строки {from}–{to}',
  'table.pageNumber': 'Страница {page}',
  'table.previousPage': 'Предыдущая',
  'table.nextPage': 'Следующая',
  'table.pagesBelow': 'Страницы, под таблицей «{table}»',
  'table.pageSizeBelow': 'Строк на странице, под таблицей «{table}»',
  'table.noMatches': 'Ни одна строка не соответствует фильтрам',
  'table.noMatchesDetail': 'Расширьте или сбросьте фильтры столбцов.',
  'table.assistantOpen': 'Опишите, что нужно найти в {table}',
  'table.assistantTitle': 'Опишите, что нужно найти',
  'table.assistantIntro': 'Скажите, что вы ищете, и столбцы {table} будут отфильтрованы за вас.',
  'table.assistantPrivacy':
    'Отправляются только заголовки столбцов и ваш текст. Ни одна строка, значение или число не покидает клинику.',
  'table.assistantLabel': 'Что вы ищете?',
  'table.assistantPlaceholder': 'зарегистрированы в этом месяце, фамилия содержит Левин',
  'table.assistantAsk': 'Предложить фильтры',
  'table.assistantRetry': 'Спросить ещё раз',
  'table.assistantApply': 'Применить фильтры',
  'table.assistantClose': 'Закрыть',
  'table.assistantWorking': 'Подбираем фильтры…',
  'table.assistantReady': 'Применение заменит действующие фильтры.',
  'table.assistantNoFilters': 'Ни один столбец этой таблицы нельзя сузить по этому запросу.',
  'table.assistantContains': 'содержит',
  'table.assistantFrom': 'от',
  'table.assistantTo': 'до',
  'table.assistantUnmatched': 'Фильтры столбцов не могут запрашивать:',
  'table.assistantKeyMissing': 'Для этой учётной записи не настроен ключ OpenAI.',
  'table.assistantForbidden': 'Этой учётной записи запрещено выполнять запросы к ИИ.',
  'table.assistantFailed': 'Не удалось предложить фильтры. Попробуйте ещё раз.',

  'shell.resizeContext': 'Ширина контекстной панели',

  'appearance.label': 'Оформление',
  'appearance.theme': 'Тема',
  'appearance.mode': 'Режим',
  'appearance.light': 'Светлый',
  'appearance.dark': 'Тёмный',
  'appearance.followTelegram': 'Как в Telegram',
  'appearance.scale': 'Размер текста',
  'appearance.scaleValue': '{percent}%',
  'appearance.density': 'Плотность',
  'appearance.densityCompact': 'Компактная',
  'appearance.densityCozy': 'Обычная',
  'appearance.densityRelaxed': 'Свободная',
  'appearance.themeTerys': 'Terys',
  'appearance.themeEmerald': 'Изумруд',
  'appearance.themeAmber': 'Янтарь',
  'appearance.themeGraphite': 'Графит',
  'appearance.themeSmartomica': 'Smartomica',
  'appearance.themeSmartomicaBrand': 'Smartomica (бренд)',

  'login.title': 'Войти',
  'login.subtitle': 'Войдите в рабочее пространство вашей клиники.',
  'login.username': 'Имя пользователя',
  'login.password': 'Пароль',
  'login.submit': 'Войти',
  'login.submitting': 'Выполняется вход',
  'login.usernameRequired': 'Введите имя пользователя.',
  'login.passwordRequired': 'Введите пароль.',
  'login.telegramNotLinked': "Сначала привяжите Telegram к учётной записи Тэрислинг в профиле. Ниже можно войти по паролю.",
  'login.telegramInactive': "Вход через Telegram для этой учётной записи недоступен. Обратитесь к администратору клиники.",
  'login.telegramExpired': "Подтверждение входа Telegram устарело или уже использовано. Нажмите кнопку события в Telegram ещё раз или войдите ниже.",
  'login.telegramInvalid': "Не удалось подтвердить вход через Telegram. Нажмите кнопку события в Telegram ещё раз или войдите ниже.",
  'login.failed': 'Не удалось войти. Проверьте имя пользователя и пароль.',
  'login.maintenanceRestricted': 'Вход ограничен на время технического обслуживания. Попробуйте войти после его завершения.',
  'login.throttled': 'Слишком много попыток входа. Попробуйте позже.',
  'login.unavailable': 'Сейчас служба недоступна. Попробуйте позже.',

  'shell.primaryNavigation': 'Основная',

  'shell.navCollapse': 'Свернуть навигацию',
  'shell.navExpand': 'Развернуть навигацию',
  'shell.navShow': 'Показать навигацию',
  'shell.navHide': 'Скрыть навигацию',
  'shell.navDrawer': 'Навигация',
  'shell.contextHide': 'Скрыть панель контекста',
  'shell.contextShow': 'Показать панель контекста',
  'shell.accountRegion': 'Учётная запись и рабочее пространство',
  'shell.activeClinic': 'Активная организация',
  'shell.clinicSwitchFailed': 'Не удалось сменить активную клинику.',
  'shell.organizationCurrent': 'Текущая',
  'shell.organizationSwitchClose': 'Закрыть',
  'shell.organizationContextChanged': 'Контекст организации изменился. Обновите его перед продолжением работы.',
  'shell.organizationContextRefresh': 'Обновить контекст организации',
  'shell.elevation': 'Расширенный доступ разработчика',
  'shell.elevationActive': 'Расширенный доступ разработчика включён',
  'shell.elevationOff': 'Расширенный доступ выключен',
  'shell.elevationEnds': 'Завершается',
  'shell.elevationNoExpiry': 'Только вручную',
  'shell.elevationDeactivate': 'Завершить расширенный доступ',
  'shell.elevationDeactivateFailed': 'Не удалось завершить расширенный доступ разработчика.',
  'shell.elevationReason': 'Причина расширенного доступа разработчика',
  'shell.elevationTemporary': 'Расширить доступ на час',
  'shell.elevationPersistent': 'Оставить доступ в dev-среде',
  'shell.elevationActivateFailed': 'Не удалось включить расширенный доступ разработчика.',
  'shell.signOut': 'Выйти',
  'shell.signingOut': 'Выход',
  'shell.sessionExpired': 'Ваш сеанс завершён. Войдите снова.',

  'shell.unsavedTitle': 'Эта работа не сохранена',
  'shell.unsavedBodyOne': 'Если уйти сейчас, будет потеряна работа, которую сервер так и не получил:',
  'shell.unsavedBodyMany':
    'Если уйти сейчас, будет потеряна работа, которую сервер так и не получил (фрагментов: {count}):',
  'shell.unsavedStay': 'Остаться на этой странице',
  'shell.unsavedDiscard': 'Отменить и уйти',

  'shell.contextPanel': 'Панель контекста',

  'notFound.title': 'Страница не найдена',
  'notFound.detail': 'Такой страницы не существует.',
  'notFound.back': 'Перейти на панель управления',

  'error.invalidReadRequest': 'Сервер отклонил запрос страницы',
  'error.invalidReadRequestDetail': 'Попросите администратора проверить адрес страницы и запрос.',
  'error.title': 'Что-то пошло не так',
  'error.detail': 'Не удалось загрузить страницу.',
  'error.retry': 'Попробуйте ещё раз',
  'error.forbidden': 'У вас нет доступа',
  'error.forbiddenDetail': 'Обратитесь к администратору, если он вам нужен.',
  'error.invalidResponse': 'Ответ сервера был неполным.',

  'error.notRefreshed': 'Не обновлено',
  'error.notRefreshedDetail': 'Показано последнее, что подтвердил сервер. Данные могли измениться.',

  'nav.patients': 'Пациенты',
  'nav.patientsDescription': 'Реестр, карты и приёмы',

  'nav.schedule': 'Расписание',
  'nav.scheduleDescription': 'День и его приёмы',

  'nav.tasksDescription': 'Работа клиники и последующие действия',

  'nav.leads': 'Заявки',
  'nav.leadsDescription': 'Новые обращения и регистрация пациентов',

  "nav.controlCenter": "Управление клиникой",
  "nav.controlCenterDescription": "Организация, сотрудники, формы и подключения",

  'nav.backToDashboard': 'Назад на панель управления',
  'nav.backToPatients': 'Назад к пациентам',
  'nav.backToPatient': 'Назад к пациенту',
  'nav.backToSchedule': 'Назад к расписанию',
  'nav.backToControlCenter': 'Назад к управлению клиникой',
  'nav.backToHelp': 'Назад к справке',

  'quickAccess.label': 'Быстрый доступ',
  'quickAccess.customize': 'Настроить быстрый доступ',
  'quickAccess.hint':
    'Выбранные разделы остаются в одном нажатии на любом экране. Перетащите плитку из панели навигации или пациента из списка и отпустите в любом месте верхней полосы — или нажмите булавку рядом: в том числе для раздела пациента или записи. Где отпустите — там она и встанет, а закладка, перетащенная вдоль строки, переезжает на новое место.',
  'quickAccess.groupWorkspace': 'Рабочее пространство',
  'quickAccess.groupProfile': 'Профиль',
  'quickAccess.groupChosen': 'В строке',
  'quickAccess.pin': 'Добавить «{name}» в быстрый доступ',
  'quickAccess.unpin': 'Убрать «{name}» из быстрого доступа',
  'quickAccess.unpinUnavailable': 'Убрать недоступную закладку из быстрого доступа',
  'quickAccess.dropHint': 'Отпустите, чтобы добавить в быстрый доступ',
  'quickAccess.moveHint': 'Отпустите, чтобы переместить сюда',
  'quickAccess.moveEarlier': 'Переместить «{name}» ближе к началу быстрого доступа',
  'quickAccess.moveLater': 'Переместить «{name}» ближе к концу быстрого доступа',
  'quickAccess.bookmarkName': '{within}: {label}',
  'quickAccess.recordName': '{subject}: {label}',
  'quickAccess.recordViewName': '{subject}: {within}, {label}',
  'quickAccess.entrySubject': '{name}, запись ЭМК {code}',
  'quickAccess.entryOnly': 'Запись ЭМК {code}',
  'quickAccess.unavailable': 'Больше недоступно',
  'common.unknownValue': 'Неизвестное значение',
  /* Как пишется номер версии — в одном месте. */
  'common.version': 'v{version}',
  /* Редакция записи — в одном месте; «версия» — выпуск программы, протокола или справочника. */
  'common.revision': 'Редакция',
  'common.revisionNumber': 'Редакция {revision}',
  'common.revisionHistory': 'История редакций',
  /* Единица размера файла — та же, что уже стоит в фильтре размера. */
  'common.bytes': '{size} Б',
  'common.kilobytes': '{size} КБ',
  'common.megabytes': '{size} МБ',
  'time.invalidLocal': 'Выберите дату и время, существующие в часовом поясе клиники.',
  'coding.loading': 'Загрузка совпадений словаря',
  'coding.empty': 'Нет совпадений в словаре',
  'coding.failed': 'Не удалось выполнить поиск в словаре.',
}
